import { useEffect, useState } from 'react';

import {
  Badge,
  Icon,
  IconButton,
  OffsetPagination,
  Table,
  TableCard,
  TableSkeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@opositandopn/ui';
import {
  IoArrowUndoOutline,
  IoClipboardOutline,
  IoReaderOutline,
} from 'react-icons/io5';

import { ResourceDialog } from '../../../../components/shared/resource-dialog/ResourceDialog';
import { useProfile } from '../../../../context/ProfileContext';
import { BaseComponentProps } from '../../../../types/base-component.types';
import {
  useGetMultipleChoiceQuestions,
  useRestoreMultipleChoiceQuestion,
} from '../../api/useMultipleChoiceQuestions';
import { MultipleChoiceQuestionView } from '../multiple-choice-question-view/MultipleChoiceQuestionView';
import {
  QuestionListFilterForm,
  toQuestionListRequest,
} from '../question-list-filter/QuestionListFilter';

import styles from './MultipleChoiceQuestionAdminList.module.scss';

type ContainerProps = BaseComponentProps & {
  filter: QuestionListFilterForm;
};

function MultipleChoiceQuestionAdminList({
  className,
  filter,
  testId = 'ui-multiple-choice-question-admin-list',
}: ContainerProps) {
  const { profile } = useProfile();
  const [current, setCurrent] = useState<string>();
  const [pagination, setPagination] = useState({ pageNumber: 1, pageSize: 20 });

  const { data: response, isLoading } = useGetMultipleChoiceQuestions({
    ...toQuestionListRequest(filter, profile.id!),
    pageNumber: pagination.pageNumber,
    pageSize: pagination.pageSize,
  });

  const { mutate: restoreMultipleChoiceQuestion, isPending } =
    useRestoreMultipleChoiceQuestion();

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
  }, [filter]);

  return (
    <>
      <TableCard.Root className={className} data-testid={testId}>
        <TableCard.Header
          title="Preguntas"
          contentTrailing={
            <Badge>
              {response?.totalItems
                ? `Total: ${response?.totalItems}`
                : 'Sin preguntas'}
            </Badge>
          }
        />
        <Table aria-label="Preguntas">
          <Table.Header>
            <Table.Head id="lesson" label="Lección" isRowHeader />
            <Table.Head id="question" label="Pregunta" />
            <Table.Head id="status" label="Estado" />
            <Table.Head id="actions" />
          </Table.Header>
          {isLoading || response === undefined ? (
            <TableSkeleton columns={4} items={pagination.pageSize} />
          ) : (
            <Table.Body
              items={response.items}
              renderEmptyState={() => 'No hay preguntas'}
            >
              {(item) => (
                <Table.Row id={item.id}>
                  <Table.Cell
                    style={{
                      width: '20%',
                      wordWrap: 'break-word',
                      textWrap: 'balance',
                      whiteSpace: 'normal',
                    }}
                  >
                    {item.lesson?.name}
                  </Table.Cell>
                  <Table.Cell
                    style={{
                      width: '100%',
                      wordWrap: 'break-word',
                      textWrap: 'pretty',
                      whiteSpace: 'normal',
                    }}
                  >
                    {item.text}
                  </Table.Cell>
                  <Table.Cell>
                    {item.status === 'draft' ? (
                      <Badge bg="warning">Borrador</Badge>
                    ) : item.status === 'removed' ? (
                      <Badge bg="danger">Eliminada</Badge>
                    ) : item.status === 'published' ? (
                      <Badge bg="success">Publicada</Badge>
                    ) : null}
                  </Table.Cell>
                  <Table.Cell>
                    <div className={styles['actions']}>
                      {item.assessments && item.assessments.length > 0 && (
                        <Tooltip placement="top">
                          <TooltipTrigger asChild={true}>
                            <Icon icon={<IoClipboardOutline />} size="sm" />
                          </TooltipTrigger>
                          <TooltipContent className={styles['assessments']}>
                            {item.assessments.map((assessment) => (
                              <Badge key={assessment.id} bg="featured">
                                {assessment.name}
                              </Badge>
                            ))}
                          </TooltipContent>
                        </Tooltip>
                      )}
                      {item.status === 'removed' && (
                        <Tooltip>
                          <TooltipTrigger asChild={true}>
                            <IconButton
                              size="sm"
                              icon={<IoArrowUndoOutline />}
                              disabled={isPending}
                              onClick={() => {
                                restoreMultipleChoiceQuestion({
                                  subjectId: filter.subject.id,
                                  id: item.id,
                                });
                              }}
                            />
                          </TooltipTrigger>
                          <TooltipContent className={styles['restore']}>
                            Restaurar pregunta
                          </TooltipContent>
                        </Tooltip>
                      )}
                      <IconButton
                        size="sm"
                        icon={<IoReaderOutline />}
                        onClick={() => {
                          setCurrent(item.id);
                        }}
                      />
                    </div>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          )}
        </Table>
        {response && (
          <OffsetPagination
            className={styles.pagination}
            pageNumber={pagination.pageNumber}
            pageSize={pagination.pageSize}
            totalRecords={response.totalItems}
            onPageChange={(pageNumber: number) => {
              setPagination({ ...pagination, pageNumber });
            }}
            pageNeighbours={2}
          />
        )}
      </TableCard.Root>
      <ResourceDialog open={current !== undefined}>
        {current && (
          <MultipleChoiceQuestionView
            subjectId={filter.subject.id}
            questionId={current}
            onClose={() => {
              setCurrent(undefined);
            }}
          />
        )}
      </ResourceDialog>
    </>
  );
}

export { MultipleChoiceQuestionAdminList };
